import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import { FaQuestion } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import { nurseAPI } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';
import PatientDetailsPopup from '../../components/PatientDetailsPopup';
import { toast } from 'react-toastify';

const TableLoader = () => (
  <div className="animate-pulse">
    <div className="bg-white rounded-lg shadow">
      <div className="grid grid-cols-4 gap-4 p-4 border-b">
        {[...Array(4)].map((_, idx) => (
          <div key={idx} className="h-4 bg-gray-200 rounded"></div>
        ))}
      </div>
      <div className="divide-y divide-gray-100">
        {[...Array(5)].map((_, idx) => (
          <div key={idx} className="p-4">
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, cellIdx) => (
                <div key={cellIdx} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Patients = () => {
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalPatients: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });

 
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');

  useEffect(() => {
    fetchPatients();
  }, [searchText, pagination.currentPage, subscriptionFilter]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user || !user.id) {
        toast.error('User information not found');
        return;
      }

      const response = await nurseAPI.getNurseAssignedPatients(user.id, {
        page: pagination.currentPage,
        limit: pagination.limit,
        search: searchText,
       
        subscription_status: subscriptionFilter === 'all' ? '' : subscriptionFilter
      });

      if (response.success) {
        setPatients(response.patients);
        setPagination(prev => ({
          ...prev,
          totalPages: response.pagination.totalPages,
          totalPatients: response.pagination.totalPatients,
          hasNextPage: response.pagination.currentPage < response.pagination.totalPages,
          hasPrevPage: response.pagination.currentPage > 1
        }));
      } else {
        toast.error('Failed to fetch patients');
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error('Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

 

  const handleSubscriptionChange = (e) => {
    setSubscriptionFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleViewProfile = (patient) => {
    setSelectedPatient(patient);
    setIsProfileModalOpen(true);
  };

  // Get unique diagnosis values
  const diagnosisOptions = ['all', ...new Set(patients.map(patient => patient.diagnosis || 'Under Review'))];

  return (
    <div className="">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">My Patients</h1>
        <p className="text-gray-600">Manage and monitor your assigned patients' progress</p>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-1 gap-4">
          <input
            type="text"
            placeholder="Search patients..."
            value={searchText}
            onChange={handleSearchChange}
            className="px-4 py-2 border rounded-md w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          
          

          {/* <select
            value={subscriptionFilter}
            onChange={handleSubscriptionChange}
            className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Subscriptions</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
          </select> */}

        
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <TableLoader />
      ) : (
        <div className="bg-white rounded-lg shadow">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Patient Name</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Diagnosis Status</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Subscription</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {patients.length > 0 ? (
                patients.map((patient) => (
                  <tr key={patient._id}>
                    <td className="py-4 px-6">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {patient.user.profileImage ? (
                            <img
                              src={getImageUrl(patient.user.profileImage)}
                              alt=""
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="h-6 w-6 text-gray-500" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {patient.user.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-900">
                        {patient.care_plan_onboarding_completed ? 'Completed' : 'Not Completed'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          patient.subscription_status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : patient.subscription_status === 'paused'
                            ? 'bg-yellow-100 text-yellow-800'
                            : patient.subscription_status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {patient.subscription_status ? patient.subscription_status.charAt(0).toUpperCase() + patient.subscription_status.slice(1) : 'N/A'}
                        </span>
                        <FaQuestion
                          size={14}
                          className="text-gray-600 border border-gray-600 rounded-full p-0.5 cursor-pointer"
                          data-tooltip-id={`subscription-tooltip-${patient._id}`}
                          data-tooltip-content={
                            patient.subscription_status === 'active' ? 'Ready for sessions' :
                            patient.subscription_status === 'paused' ? 'Subscription temporarily suspended' :
                            patient.subscription_status === 'cancelled' ? 'Subscription has been cancelled' :
                            'No subscription status'
                          }
                        />
                        <Tooltip id={`subscription-tooltip-${patient._id}`} place="top" effect="solid" />
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <button
                        onClick={() => handleViewProfile(patient)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View Profile
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                    <p className="text-lg font-medium text-gray-900">No patients found</p>
                    <p className="mt-1">Try adjusting your filters or search term.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && patients.length > 0 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing 1 to {Math.min(pagination.limit, pagination.totalPatients)} of {pagination.totalPatients} results
          </div>
          <div className="flex items-center">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="px-2 py-1 border rounded-l text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            {[...Array(pagination.totalPages)].map((_, idx) => {
              const pageNumber = idx + 1;
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-3 py-1 border-t border-b ${
                    pagination.currentPage === pageNumber
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="px-2 py-1 border rounded-r text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Patient Details Modal */}
      {isProfileModalOpen && selectedPatient && (
        <PatientDetailsPopup
          patient={selectedPatient}
          onClose={() => {
            setIsProfileModalOpen(false);
            setSelectedPatient(null);
          }}
        />
      )}
    </div>
  );
};

export default Patients; 